import { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { GridRenderEditCellParams, Toolbar } from '@mui/x-data-grid';

import { getVehicleTypes } from '@/generated/client/targetattributes/targetattributes';
import { VehicleTypeDto, VehicleTypeDtoVehicleCategory } from '@/generated/model';
import { useTranslation } from 'react-i18next';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { SearchComponent } from '../common/Search';
import DisplayNoResult from '../common/DisplayNoResult';

export default function VehicleTypeEditor({ params }: { params: GridRenderEditCellParams }) {
  const { id, field, api } = params;
  const { t } = useTranslation();
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeDto[] | undefined>();
  const [open, setOpen] = useState(true);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [originalRows, setOriginalRows] = useState<VehicleTypeDto[]>([]);
  async function loadPossibleValues() {
    const newValues = (await getVehicleTypes()).data;
    setVehicleTypes(newValues);
    setOriginalRows(newValues);
  }

  useEffect(() => {
    loadPossibleValues();
  }, []);

  useEffect(() => {
    if (!searchText) {
      setVehicleTypes(originalRows);
      return;
    }
    const lower = searchText.toLowerCase();
    const filtered = originalRows.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(lower))
    );
    setVehicleTypes(filtered);
  }, [searchText, originalRows]);

  if (!vehicleTypes) {
    return <span className="target-attribute-value disabled">{t('targetAttributes.pleaseWait')}</span>;
  }

  const columns = [
    {
      field: 'brand',
      headerName: t('targetAttributes.vehicleBrand'),
      resizable: false,
      flex: 1,
    },
    {
      field: 'type',
      headerName: t('targetAttributes.vehicleType'),
      resizable: false,
      flex: 1,
    },
    {
      field: 'vehicleCategory',
      headerName: t('targetAttributes.vehicleCategory'),
      resizable: false,
      flex: 1,
      valueFormatter: (value?: VehicleTypeDtoVehicleCategory) => {
        if (!value) {
          return '';
        }
        return t('targetAttributes.category.' + value);
      },
    },
  ];

  const handleRowClick = (params: { row: any }) => {
    api.setEditCellValue({
      id,
      field,
      value: params.row,
    });
    setOpen(false);
    api.stopRowEditMode({ id, field });
  };

  function CustomToolbar() {
    return (
      <Toolbar>
        <Box className={'targetattribute-vehicletype-toolbar'}>
          <SearchComponent
            searchText={searchText}
            setSearchText={setSearchText}
            showHistory={false}
            placeholder={t('targetAttributes.searchPlaceHolder')}
          />
        </Box>
      </Toolbar>
    );
  }

  return (
    <Dialog disableEscapeKeyDown open={open} onClose={() => api.stopRowEditMode({ id, field })} maxWidth="md" fullWidth>
      <DialogContent className="targetattributes-vehicletype-content">
        <Typography variant="h6"> {t('targetAttributes.vehicleCategoryMainText')}</Typography>
        <Typography variant="caption"> {t('targetAttributes.vehicleCategorySubText')}</Typography>
        <DataGridPro
          className="targetattributes-vehicletype-grid"
          rows={vehicleTypes}
          columns={columns}
          onRowClick={handleRowClick}
          hideFooter
          showToolbar
          slots={{
            toolbar: CustomToolbar,
            noRowsOverlay: () => <DisplayNoResult searchText={searchText} />,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
